import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/useAuth.js';
import { useToolHolders, useOutstandingCheckouts, useCheckoutTool } from '../../custody/hooks/useCustody.js';

export interface CheckoutModalProps {
  readonly shopId:    string;
  readonly machineId: string;
  readonly slot:      number;
  readonly onClose:   () => void;
}

function pocketLabel(n: number): string {
  return `T${String(n).padStart(2, '0')}`;
}

export function CheckoutModal({ shopId, machineId, slot, onClose }: CheckoutModalProps): JSX.Element {
  const { displayName } = useAuth();
  const holders   = useToolHolders(shopId);
  const outstanding = useOutstandingCheckouts(shopId);
  const checkout  = useCheckoutTool(shopId);

  const checkedOutIds = new Set((outstanding.data ?? []).map(c => c.holder_id));
  const available = (holders.data ?? []).filter(h => !checkedOutIds.has(h.holder_id));

  const [holderId, setHolderId]   = useState('');
  const [takenBy, setTakenBy]     = useState(displayName ?? '');
  const [error, setError]         = useState<string | null>(null);

  const isLoading = holders.isLoading || outstanding.isLoading;

  async function handleSubmit() {
    if (!holderId) { setError('Select a holder'); return; }
    if (!takenBy.trim()) { setError('Enter your name'); return; }
    setError(null);
    try {
      await checkout.mutateAsync({
        holder_id:       holderId,
        from_machine_id: machineId,
        from_slot:       slot,
        checked_out_by:  takenBy.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-84 max-w-[90vw] rounded-md border border-border bg-card p-5 shadow-xl space-y-4">
        <div>
          <h3 className="font-mono text-sm font-semibold">Check Out Tool</h3>
          <p className="text-[11px] font-mono text-text-dim mt-0.5">
            {pocketLabel(slot)} &mdash; {machineId}
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-text-dim mb-1">
              Holder
            </label>
            {isLoading ? (
              <div className="h-8 rounded bg-surface animate-pulse" />
            ) : available.length === 0 ? (
              <p className="text-xs font-mono text-muted italic">
                No available holders. All are checked out or none exist.
              </p>
            ) : (
              <select
                value={holderId}
                onChange={e => setHolderId(e.target.value)}
                className="w-full rounded border border-border bg-surface px-2 py-1.5 text-xs font-mono text-text focus:outline-none focus:border-accent"
              >
                <option value="">Select holder…</option>
                {available.map(h => (
                  <option key={h.holder_id} value={h.holder_id}>
                    {h.description} — {h.type}
                    {h.diameter_mm != null ? ` · ${h.diameter_mm}mm` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-text-dim mb-1">
              Checked out by
            </label>
            <input
              type="text"
              value={takenBy}
              onChange={e => setTakenBy(e.target.value)}
              placeholder="Your name"
              className="w-full rounded border border-border bg-surface px-2 py-1.5 text-xs font-mono placeholder:text-text-dim focus:outline-none focus:border-accent"
            />
          </div>
        </div>

        {error && (
          <p className="text-xs font-mono text-red">{error}</p>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-mono rounded border border-border hover:bg-surface transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { void handleSubmit(); }}
            disabled={checkout.isPending || isLoading || available.length === 0}
            className="px-3 py-1.5 text-xs font-mono rounded bg-accent text-black hover:brightness-110 disabled:opacity-50 transition-all"
          >
            {checkout.isPending ? 'Checking out…' : 'Check Out'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
